import {
  BUSINESS_NAME_MAX_LENGTH,
  REGISTRATION_NUMBER_MAX_LENGTH,
} from '@pulsify/shared/validation'
import { Loader2 } from 'lucide-react'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { toast } from 'sonner'
import { isValidVatInput } from '~/entities/business/lib/company-details'
import { useUpdateCompanyDetails } from '~/entities/business/model/use-business'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import type { Business } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'

interface CompanyDetailsFormProps {
  business: Business
  onSaved?: () => void
  secondaryAction?: ReactNode
}

/** Official name, register number and UID of the business; only the name is required. */
export function CompanyDetailsForm({
  business,
  onSaved,
  secondaryAction,
}: CompanyDetailsFormProps) {
  const save = useUpdateCompanyDetails(business.id)
  const ids = { legalName: useId(), registrationNumber: useId(), vatId: useId() }
  const [legalName, setLegalName] = useState(
    business.companyDetailsPending ? '' : business.legalName,
  )
  const [registrationNumber, setRegistrationNumber] = useState(business.registrationNumber ?? '')
  const [vatId, setVatId] = useState(business.vatId ?? '')
  const [submitted, setSubmitted] = useState(false)

  const legalNameError = submitted && !legalName.trim() ? m.companyDetailsLegalNameRequired() : null
  const vatIdError = submitted && !isValidVatInput(vatId) ? m.companyDetailsVatIdInvalid() : null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (!legalName.trim() || !isValidVatInput(vatId)) return
    save.mutate(
      {
        legalName: legalName.trim(),
        registrationNumber: registrationNumber.trim() || null,
        vatId: vatId.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(m.companyDetailsSaved())
          onSaved?.()
        },
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={ids.legalName}>{m.companyDetailsLegalName()}</Label>
        <Input
          id={ids.legalName}
          value={legalName}
          maxLength={BUSINESS_NAME_MAX_LENGTH}
          placeholder={m.companyDetailsLegalNamePlaceholder()}
          autoComplete="organization"
          aria-invalid={legalNameError ? true : undefined}
          onChange={(event) => setLegalName(event.target.value)}
        />
        {legalNameError ? <p className="text-destructive text-sm">{legalNameError}</p> : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor={ids.registrationNumber}>
          {m.companyDetailsRegistrationNumber()}
          <span className="font-normal text-muted-foreground">{m.optionalField()}</span>
        </Label>
        <Input
          id={ids.registrationNumber}
          value={registrationNumber}
          maxLength={REGISTRATION_NUMBER_MAX_LENGTH}
          placeholder={m.companyDetailsRegistrationNumberPlaceholder()}
          onChange={(event) => setRegistrationNumber(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={ids.vatId}>
          {m.companyDetailsVatId()}
          <span className="font-normal text-muted-foreground">{m.optionalField()}</span>
        </Label>
        <Input
          id={ids.vatId}
          value={vatId}
          placeholder={m.atu12345678()}
          aria-invalid={vatIdError ? true : undefined}
          onChange={(event) => setVatId(event.target.value)}
        />
        {vatIdError ? <p className="text-destructive text-sm">{vatIdError}</p> : null}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        {secondaryAction}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="animate-spin" /> : null}
          {m.save()}
        </Button>
      </div>
    </form>
  )
}
